import { SyncOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styles from './configuration-progress.module.scss';

/**
 * Configuration Progress Component
 *
 * A reusable component that displays a progress bar with a title and status.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the progress bar
 * @param {React.ReactNode} props.icon - The icon to display next to the title
 * @param {string} props.status - The status text to display
 * @param {number} props.progress - The progress value (0-100)
 * @param {string} props.activeColor - The color of the progress bar when active
 * @param {string} props.inactiveColor - The color of the progress bar when inactive
 * @param {boolean} props.isActive - Whether the progress bar is active
 * @param {number} props.configuredSettings - Number of configured settings
 * @param {number} props.totalSettings - Total number of settings
 * @param {boolean} props.isEnabled - Whether the feature is enabled
 * @param {string} props.disabledMessage - Message to display when feature is disabled
 */
export const ConfigurationProgress = ({
  title = 'Configuration Progress',
  icon = <SyncOutlined className={styles.progressIcon} />,
  status,
  progress = 0,
  activeColor = '#00506C',
  inactiveColor = '#d9d9d9',
  isActive = true,
  configuredSettings,
  totalSettings = 3,
  isEnabled = true,
  disabledMessage = 'Feature disabled',
}) => {
  // If status is not provided but we have configuredSettings and totalSettings, generate a status
  const displayStatus =
    status || (!isEnabled ? disabledMessage : `${configuredSettings} of ${totalSettings} settings configured`);

  // If progress is not provided but we have configuredSettings and totalSettings, calculate progress
  const displayProgress =
    progress || (configuredSettings !== undefined ? (configuredSettings / totalSettings) * 100 : 0);

  return (
    <div className={styles.progressContainer}>
      <div className={styles.progressHeader}>
        <div className={styles.progressTitle}>
          {icon}
          {title}
        </div>
        <div className={styles.progressStatus}>{displayStatus}</div>
      </div>
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{
            width: `${displayProgress}%`,
            backgroundColor: isActive && isEnabled ? activeColor : inactiveColor,
          }}
        />
      </div>
    </div>
  );
};

ConfigurationProgress.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
  status: PropTypes.string,
  progress: PropTypes.number,
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  isActive: PropTypes.bool,
  configuredSettings: PropTypes.number,
  totalSettings: PropTypes.number,
  isEnabled: PropTypes.bool,
  disabledMessage: PropTypes.string,
};
